
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface UserPresence {
  user_id: string;
  online_at: string;
  status: 'online' | 'offline';
  first_name?: string;
  last_name?: string;
  presence_ref?: string;
}

export const usePresence = () => {
  const { user } = useAuth();
  const [isOnline, setIsOnline] = useState(false);
  const [lastSeen, setLastSeen] = useState<Date | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<Record<string, UserPresence[]>>({});

  useEffect(() => {
    if (!user) return;

    let channel: any = null;

    const setupPresence = async () => {
      try {
        // Create a single shared channel for all presence tracking
        channel = supabase.channel('global_user_presence', {
          config: {
            presence: {
              key: user.id,
            },
          },
        });

        const userStatus: UserPresence = {
          user_id: user.id,
          online_at: new Date().toISOString(),
          status: 'online',
          first_name: user.user_metadata?.first_name,
          last_name: user.user_metadata?.last_name
        };

        channel
          .on('presence', { event: 'sync' }, () => {
            const newState = channel.presenceState() as Record<string, UserPresence[]>;
            console.log('Global presence sync:', newState);
            setOnlineUsers(newState);
            
            // Check if current user is in the presence state
            const userPresence = newState[user.id];
            const currentlyOnline = !!userPresence && userPresence.length > 0;
            setIsOnline(currentlyOnline);
            
            if (currentlyOnline) {
              setLastSeen(new Date());
            }
          })
          .on('presence', { event: 'join' }, ({ key, newPresences }) => {
            console.log('User joined presence:', key, newPresences);
            // Always use presenceState() as source of truth after any event
            const newState = channel.presenceState() as Record<string, UserPresence[]>;
            setOnlineUsers(newState);
            
            if (key === user.id) {
              setIsOnline(true);
              setLastSeen(new Date());
            }
          })
          .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
            console.log('User left presence:', key, leftPresences);
            // Always use presenceState() as source of truth after any event
            const newState = channel.presenceState() as Record<string, UserPresence[]>;
            setOnlineUsers(newState);
            
            if (key === user.id) {
              const stillOnline = !!(newState[user.id] && newState[user.id].length > 0);
              setIsOnline(stillOnline);
              setLastSeen(new Date());
            }
          })
          .subscribe(async (status) => {
            console.log('Presence subscription status:', status);
            if (status === 'SUBSCRIBED') {
              console.log('Starting to track presence for user:', user.id);
              const presenceTrackStatus = await channel.track(userStatus);
              console.log('Presence track status:', presenceTrackStatus);
              if (presenceTrackStatus === 'ok') {
                setIsOnline(true);
                setLastSeen(new Date());
              }
            } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              console.error('Presence subscription failed:', status);
              // Retry connection after a delay
              setTimeout(() => {
                if (channel) {
                  console.log('Retrying presence subscription...');
                  channel.subscribe();
                }
              }, 3000);
            }
          });

        // Handle page visibility changes with better error handling
        const handleVisibilityChange = async () => {
          if (!channel) return;
          
          try {
            if (document.hidden) {
              console.log('Page hidden, stopping presence tracking');
              setIsOnline(false);
              setLastSeen(new Date());
              await channel.untrack();
            } else {
              console.log('Page visible, resuming presence tracking');
              const presenceTrackStatus = await channel.track(userStatus);
              console.log('Visibility change - track status:', presenceTrackStatus);
              if (presenceTrackStatus === 'ok') {
                setIsOnline(true);
                setLastSeen(new Date());
              }
            }
          } catch (error) {
            console.error('Error handling visibility change:', error);
          }
        };

        // Handle beforeunload with better error handling
        const handleBeforeUnload = async () => {
          if (!channel) return;
          
          try {
            console.log('Page unloading, stopping presence tracking');
            setIsOnline(false);
            await channel.untrack();
          } catch (error) {
            console.error('Error handling before unload:', error);
          }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Cleanup function
        return {
          cleanup: () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (channel) {
              console.log('Cleaning up presence channel');
              channel.untrack();
              supabase.removeChannel(channel);
            }
          }
        };

      } catch (error) {
        console.error('Error setting up presence:', error);
        return { cleanup: () => {} };
      }
    };

    const presenceSetup = setupPresence();

    return () => {
      presenceSetup.then(setup => setup?.cleanup());
    };
  }, [user]);

  return { isOnline, lastSeen, onlineUsers };
};
